import React, { useCallback } from "react";

/**
 * 🖱️ Hook para Drag & Drop
 * 
 * Gerencia o estado e handlers para funcionalidade de arrastar e soltar arquivos.
 * Fornece feedback visual quando arquivos são arrastados sobre a área.
 * 
 * @param {Function} onDrop - Callback executado quando arquivos são soltos
 * @param {Function} setDragActive - Setter para controlar estado visual do drag
 * @returns {Object} Handlers para eventos de drag e drop
 */
export const useDragAndDrop = (onDrop, setDragActive) => {
  const handleDrag = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
    } else if (event.type === "dragleave") {
      setDragActive(false);
    }
  }, [setDragActive]);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      if (onDrop) {
        onDrop(event.dataTransfer.files);
      }
    }
  }, [onDrop, setDragActive]);

  return {
    handleDrag,
    handleDrop
  };
};

/**
 * 🎬 Componente DragDropOverlay
 * 
 * Overlay visual mostrado quando arquivos são arrastados sobre a área.
 * 
 * @param {boolean} dragActive - Se o drag está ativo
 * @param {Object} classes - Classes de estilo
 * @param {Function} handleDrag - Handler de eventos drag
 * @param {Function} handleDrop - Handler de drop
 * @param {string} message - Mensagem a ser exibida
 */
export const DragDropOverlay = ({ 
  dragActive, 
  classes, 
  handleDrag, 
  handleDrop, 
  message = "Solte o arquivo aqui" 
}) => {
  if (!dragActive) {
    return null;
  }

  return (
    <div
      className={classes.dragElement}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      {message}
    </div>
  );
};
